import {
  DEFAULT_SETTINGS_STATE,
  SettingsStateSchema,
  type SettingsState,
} from '@triedge/staff-settings-domain';

export type DomainRecord = Record<string, unknown>;

export const asRecord = (value: unknown): DomainRecord =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as DomainRecord)
    : {};

export const cloneValue = <T>(value: T): T => {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
};

export const applyDomainPatch = (base: DomainRecord, patch: DomainRecord): DomainRecord => {
  const next: DomainRecord = { ...base };
  for (const [key, patchValue] of Object.entries(patch)) {
    if (Array.isArray(patchValue)) {
      next[key] = cloneValue(patchValue);
      continue;
    }
    if (patchValue && typeof patchValue === 'object') {
      next[key] = applyDomainPatch(asRecord(next[key]), asRecord(patchValue));
      continue;
    }
    next[key] = patchValue;
  }
  return next;
};

export const parsePayloadRecord = (value: unknown): DomainRecord => {
  if (typeof value === 'string') {
    try {
      return asRecord(JSON.parse(value));
    } catch {
      return {};
    }
  }
  return asRecord(value);
};

export const parseSettingsState = (rawPayload: unknown): SettingsState => {
  let parsedPayload: unknown = rawPayload;
  if (typeof rawPayload === 'string') {
    try {
      parsedPayload = JSON.parse(rawPayload);
    } catch {
      parsedPayload = undefined;
    }
  }

  const parsed = SettingsStateSchema.safeParse(parsedPayload);
  if (parsed.success) {
    return parsed.data;
  }
  const normalizedPayload = applyDomainPatch(
    asRecord(cloneValue(DEFAULT_SETTINGS_STATE) as unknown),
    asRecord(parsedPayload),
  );
  const normalized = SettingsStateSchema.safeParse(normalizedPayload);
  if (normalized.success) {
    return normalized.data;
  }
  return cloneValue(DEFAULT_SETTINGS_STATE);
};

export const buildTopLevelPatch = (
  previous: SettingsState,
  next: SettingsState
): Record<string, unknown> => {
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(next)) {
    const previousValue = (previous as Record<string, unknown>)[key];
    const nextValue = (next as Record<string, unknown>)[key];
    if (JSON.stringify(previousValue) !== JSON.stringify(nextValue)) {
      patch[key] = nextValue;
    }
  }
  return patch;
};
